import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Play, Trash2, Loader2, ArrowLeft, ChevronRight, ChevronLeft, Download, FolderDown } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { API, useAuth } from "@/App";
import { authHeaders } from "@/lib/firebase";
import { toast } from "sonner";
import JSZip from "jszip";
import { saveAs } from "file-saver";

const MemoriesPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [deleting, setDeleting] = useState(null);

  // Get current media from all memories flat list
  const selectedMedia = selectedIndex !== null ? memories[selectedIndex] : null;

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
      return;
    }
    if (user) {
      fetchMemories();
    }
  }, [user, authLoading]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedIndex === null) return;
      if (e.key === "ArrowLeft") {
        goToPrevious();
      } else if (e.key === "ArrowRight") {
        goToNext();
      } else if (e.key === "Escape") {
        setSelectedIndex(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedIndex, memories.length]);

  const fetchMemories = async () => {
    try {
      const headers = await authHeaders();
      const response = await fetch(`${API}/users/me/memories`, { headers });
      if (response.ok) {
        const data = await response.json();
        setMemories(data.memories || []);
      }
    } catch (error) {
      console.error("Failed to fetch memories:", error);
    } finally {
      setLoading(false);
    }
  };

  const goToPrevious = () => {
    if (selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
    }
  };

  const goToNext = () => {
    if (selectedIndex < memories.length - 1) {
      setSelectedIndex(selectedIndex + 1);
    }
  };

  const handleDownload = async (media) => {
    try {
      const response = await fetch(media.file_url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ext = media.file_type === "video" ? "mp4" : "jpg";
      a.download = `${media.game_title?.replace(/[^a-z0-9]/gi, '_') || 'memory'}_${media.media_id}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (error) {
      toast.error("Failed to download");
    }
  };

  const [downloadingEvent, setDownloadingEvent] = useState(null);

  const handleBulkDownload = async (game) => {
    setDownloadingEvent(game.game_id);
    const toastId = toast.loading(`Preparing ${game.media.length} files...`);
    
    try {
      // Check if on iOS with Web Share API support
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const canShareFiles = navigator.canShare && navigator.share;
      
      // Fetch all files first
      const files = [];
      let completed = 0;
      
      for (const media of game.media) {
        try {
          const response = await fetch(media.file_url);
          const blob = await response.blob();
          const ext = media.file_type === "video" ? "mp4" : "jpg";
          const filename = `${game.game_title?.replace(/[^a-z0-9]/gi, '_') || 'photo'}_${completed + 1}.${ext}`;
          const file = new File([blob], filename, { type: blob.type });
          files.push(file);
          completed++;
          toast.loading(`Downloading ${completed}/${game.media.length}...`, { id: toastId });
        } catch (err) {
          console.error(`Failed to download ${media.media_id}:`, err);
        }
      }
      
      // On iOS, try to use Web Share API so users can save to Photos
      if (isIOS && canShareFiles && files.length > 0) {
        try {
          if (navigator.canShare({ files })) {
            toast.loading("Opening share menu...", { id: toastId });
            await navigator.share({
              files,
              title: `${game.game_title || 'Event'} Photos`,
            });
            toast.success(`${completed} files ready to save!`, { id: toastId });
            setDownloadingEvent(null);
            return;
          }
        } catch (shareErr) {
          if (shareErr.name === 'AbortError') {
            toast.dismiss(toastId);
            setDownloadingEvent(null);
            return;
          }
          console.log("Share failed, falling back to ZIP:", shareErr);
        }
      }
      
      // Fallback: Create ZIP file
      toast.loading("Creating zip file...", { id: toastId });
      const zip = new JSZip();
      const folder = zip.folder(game.game_title?.replace(/[^a-z0-9]/gi, '_') || 'event');
      
      for (let i = 0; i < files.length; i++) {
        folder.file(files[i].name, files[i]);
      }
      
      const content = await zip.generateAsync({ type: "blob" });
      const zipName = `${game.game_title?.replace(/[^a-z0-9]/gi, '_') || 'event'}_photos.zip`;
      saveAs(content, zipName);
      
      toast.success(`Downloaded ${completed} files!`, { id: toastId });
    } catch (error) {
      console.error("Bulk download failed:", error);
      toast.error("Failed to create download", { id: toastId });
    } finally {
      setDownloadingEvent(null);
    }
  };

  const handleDelete = async (media) => {
    setDeleting(media.media_id);
    try {
      const headers = await authHeaders();
      const response = await fetch(`${API}/games/${media.game_id}/gallery/${media.media_id}`, {
        method: "DELETE",
        headers
      });
      if (response.ok) {
        const newMemories = memories.filter(m => m.media_id !== media.media_id);
        setMemories(newMemories);
        toast.success("Photo deleted");
        // Close or move to next/previous
        if (newMemories.length === 0) {
          setSelectedIndex(null);
        } else if (selectedIndex >= newMemories.length) {
          setSelectedIndex(newMemories.length - 1);
        }
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to delete");
      }
    } catch (error) {
      toast.error("Failed to delete");
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };

  // Check if user can delete this media (uploader or event host)
  const canDelete = (media) => {
    if (!user) return false;
    // User uploaded it
    if (media.user_id === user.user_id) return true;
    // User is the event host (we'd need owner_id from game, but for now check if they hosted)
    // The backend will verify this anyway
    return false;
  };

  // Group memories by game/event
  const memoriesByGame = memories.reduce((acc, media, globalIndex) => {
    const gameId = media.game_id;
    if (!acc[gameId]) {
      acc[gameId] = {
        game_id: gameId,
        game_title: media.game_title,
        game_date: media.game_date,
        game_sport: media.game_sport,
        media: []
      };
    }
    acc[gameId].media.push({ ...media, globalIndex });
    return acc;
  }, {});

  const gamesList = Object.values(memoriesByGame).sort((a, b) => 
    new Date(b.game_date || 0) - new Date(a.game_date || 0)
  );

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={() => navigate("/profile")} className="p-2 -ml-2 hover:bg-muted rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-primary" />
            <h1 className="font-display text-xl font-bold">My Memories</h1>
            <span className="text-sm text-muted-foreground">({memories.length})</span>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {gamesList.length === 0 ? (
          <div className="text-center py-12">
            <Camera className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
            <p className="text-muted-foreground">No memories yet</p>
            <p className="text-sm text-muted-foreground mt-1">Photos from events you attend will appear here</p>
          </div>
        ) : (
          <div className="space-y-8">
            {gamesList.map((game) => (
              <div key={game.game_id}>
                {/* Event header with download button */}
                <div className="flex items-center gap-2 mb-3">
                  <button 
                    onClick={() => navigate(`/game/${game.game_id}`)}
                    className="flex items-center gap-2 flex-1 group"
                  >
                    <div className="flex-1 text-left">
                      <h2 className="font-semibold group-hover:text-primary transition-colors">{game.game_title}</h2>
                      <p className="text-sm text-muted-foreground">{formatDate(game.game_date)} · {game.media.length} {game.media.length === 1 ? 'photo' : 'photos'}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </button>
                  
                  {/* Bulk download button */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleBulkDownload(game)}
                    disabled={downloadingEvent === game.game_id}
                    className="shrink-0"
                  >
                    {downloadingEvent === game.game_id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <><FolderDown className="w-4 h-4 mr-1" />Download All</>
                    )}
                  </Button>
                </div>
                
                {/* Event photos grid - Instagram style */}
                <div className="grid grid-cols-3 gap-0.5">
                  {game.media.map((media) => (
                    <button
                      key={media.media_id}
                      onClick={() => setSelectedIndex(media.globalIndex)}
                      className="relative aspect-square overflow-hidden bg-muted group"
                    >
                      {media.file_type === "video" ? (
                        <>
                          <video src={media.file_url} className="w-full h-full object-cover" />
                          <div className="absolute top-2 right-2">
                            <Play className="w-5 h-5 text-white drop-shadow-lg" fill="white" />
                          </div>
                        </>
                      ) : (
                        <img src={media.file_url} alt="" className="w-full h-full object-cover" />
                      )}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Media Viewer Dialog with navigation */}
      <Dialog open={selectedIndex !== null} onOpenChange={() => setSelectedIndex(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden bg-card border-border">
          {selectedMedia && (
            <>
              {/* Media with nav arrows */}
              <div className="relative bg-black aspect-square sm:aspect-video">
                {selectedMedia.file_type === "video" ? (
                  <video
                    key={selectedMedia.media_id}
                    src={selectedMedia.file_url}
                    controls
                    autoPlay
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <img
                    key={selectedMedia.media_id}
                    src={selectedMedia.file_url}
                    alt=""
                    className="w-full h-full object-contain"
                  />
                )}
                
                {/* Left arrow */}
                {selectedIndex > 0 && (
                  <button
                    onClick={goToPrevious}
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
                  >
                    <ChevronLeft className="w-6 h-6 text-white" />
                  </button>
                )}
                
                {/* Right arrow */}
                {selectedIndex < memories.length - 1 && (
                  <button
                    onClick={goToNext}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
                  >
                    <ChevronRight className="w-6 h-6 text-white" />
                  </button>
                )}
                
                {/* Counter */}
                <div className="absolute top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/50 rounded-full text-white text-sm">
                  {selectedIndex + 1} / {memories.length}
                </div>
              </div>
              
              {/* Footer with event info, download, and delete */}
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <button 
                    onClick={() => {
                      setSelectedIndex(null);
                      navigate(`/game/${selectedMedia.game_id}`);
                    }}
                    className="text-left hover:text-primary transition-colors"
                  >
                    <p className="font-medium text-sm">{selectedMedia.game_title}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(selectedMedia.game_date)}</p>
                  </button>
                  
                  <div className="flex items-center gap-2">
                    {/* Download button */}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDownload(selectedMedia)}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                    
                    {/* Delete button - only for uploader */}
                    {user && selectedMedia.user_id === user.user_id && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => handleDelete(selectedMedia)}
                        disabled={deleting === selectedMedia.media_id}
                      >
                        {deleting === selectedMedia.media_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <><Trash2 className="w-4 h-4 mr-1" />Delete</>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MemoriesPage;
